import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon, FilterIcon, XIcon, MapPinIcon, MapIcon, GlobeIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { mouzaData, samplePlots, filterData } from '../data/sampleData';
export function MouzaMapPage({
  navigateTo,
  setSelectedPlotId,
  filterState,
  setFilterState
}: {
  navigateTo: (page: string) => void;
  setSelectedPlotId: (id: string) => void;
  filterState?: {
    selectedDistrict: string;
    selectedUpazila: string;
    selectedMouza: string;
  };
  setFilterState?: (state: {
    selectedDistrict: string;
    selectedUpazila: string;
    selectedMouza: string;
  }) => void;
}) {
  const {
    language,
    toggleLanguage,
    t
  } = useLanguage();
  // Use lifted state if available, otherwise fall back to local state
  const [localDistrict, setLocalDistrict] = useState(filterState?.selectedDistrict || '');
  const [localUpazila, setLocalUpazila] = useState(filterState?.selectedUpazila || '');
  const [localMouza, setLocalMouza] = useState(filterState?.selectedMouza || '');
  const selectedDistrict = filterState?.selectedDistrict ?? localDistrict;
  const selectedUpazila = filterState?.selectedUpazila ?? localUpazila;
  const selectedMouza = filterState?.selectedMouza ?? localMouza;
  const updateFilter = (district: string, upazila: string, mouza: string) => {
    if (setFilterState) {
      setFilterState({
        selectedDistrict: district,
        selectedUpazila: upazila,
        selectedMouza: mouza
      });
    } else {
      setLocalDistrict(district);
      setLocalUpazila(upazila);
      setLocalMouza(mouza);
    }
  };
  const setSelectedDistrict = (v: string) => updateFilter(v, '', '');
  const setSelectedUpazila = (v: string) => updateFilter(selectedDistrict, v, '');
  const setSelectedMouza = (v: string) => updateFilter(selectedDistrict, selectedUpazila, v);
  const [searchKhatian, setSearchKhatian] = useState('');
  const [searchDag, setSearchDag] = useState('');
  // Map States
  const [activeMouza, setActiveMouza] = useState<any>(null);
  const [hoveredPlot, setHoveredPlot] = useState<string | null>(null);
  const [activePin, setActivePin] = useState<string | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  // Restore active mouza from filter state on mount
  useEffect(() => {
    if (selectedMouza) {
      const mouza = mouzaData.find(m => m.name === selectedMouza);
      if (mouza) setActiveMouza(mouza);
    }
  }, []);
  // Derived Filter Options
  const availableUpazilas = selectedDistrict ? filterData.districts.find(d => d.name === selectedDistrict)?.upazilas || [] : [];
  const availableMouzas = selectedUpazila ? filterData.upazilas[selectedUpazila as keyof typeof filterData.upazilas]?.mouzas || [] : [];
  const handleApplyFilter = () => {
    if (selectedMouza) {
      const mouza = mouzaData.find(m => m.name === selectedMouza);
      setActiveMouza(mouza || null);
      setActivePin(null);
      setIsMobileFilterOpen(false);
    }
  };
  const handleResetFilter = () => {
    updateFilter('', '', '');
    setSearchKhatian('');
    setSearchDag('');
    setActiveMouza(null);
    setActivePin(null);
  };
  const handlePinClick = (e: React.MouseEvent, plotId: string) => {
    e.stopPropagation();
    setActivePin(plotId === activePin ? null : plotId);
  };
  const handleViewDetails = (plotId: string) => {
    setSelectedPlotId(plotId);
    navigateTo('landRecordDetails');
  };
  const getPinColor = (type: string) => {
    switch (type) {
      case 'agricultural':
        return 'text-[#4CAF50]';
      case 'residential':
        return 'text-[#2196F3]';
      case 'commercial':
        return 'text-[#FF9800]';
      default:
        return 'text-[#9E9E9E]';
    }
  };
  return <div className="h-screen flex flex-col bg-[#F8FAFB] overflow-hidden font-sans" data-id="element-1665">
      {/* Minimal Header */}
      <header className="bg-[#006A4E] text-white h-14 flex items-center justify-between px-4 flex-shrink-0 z-30 shadow-md" data-id="element-1666">
        <div className="flex items-center space-x-4" data-id="element-1667">
          <button onClick={() => navigateTo('home')} className="flex items-center text-green-100 hover:text-white transition-colors" data-id="element-1668">
            <ArrowLeftIcon className="w-5 h-5 mr-1" data-id="element-1669" />
            <span className="hidden sm:inline font-medium" data-id="element-1670">
              {t('publicHome')}
            </span>
          </button>
          <div className="h-6 w-px bg-green-700 hidden sm:block" data-id="element-1671"></div>
          <div className="flex items-center space-x-2" data-id="element-1672">
            <MapIcon className="w-5 h-5 text-[#C9A84C]" data-id="element-1673" />
            <span className="font-bold text-lg" data-id="element-1674">{t('mouzaMap')}</span>
          </div>
        </div>
        <button onClick={toggleLanguage} className="flex items-center space-x-1 text-sm font-medium text-green-100 hover:text-white transition-colors" data-id="element-1675">
          <GlobeIcon className="w-4 h-4" data-id="element-1676" />
          <span data-id="element-1677">{language === 'bn' ? 'English' : 'বাংলা'}</span>
        </button>
      </header>

      <div className="flex-1 flex flex-col md:flex-row relative overflow-hidden" data-id="element-1678">
        {/* Mobile Filter Toggle */}
        <div className="md:hidden bg-white border-b border-gray-200 p-3 flex justify-between items-center z-20 shadow-sm" data-id="element-1679">
          <span className="font-bold text-gray-800" data-id="element-1680">
            {activeMouza ? language === 'bn' ? activeMouza.nameBn : activeMouza.name : t('selectMouzaPrompt')}
          </span>
          <button onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)} className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 rounded-md text-sm font-medium text-gray-700" data-id="element-1681">
            <FilterIcon className="w-4 h-4" data-id="element-1682" />
            <span data-id="element-1683">Filters</span>
            <ChevronDownIcon className={`w-4 h-4 transform transition-transform ${isMobileFilterOpen ? 'rotate-180' : ''}`} data-id="element-1684" />
          </button>
        </div>

        {/* Left Filter Panel */}
        <div className={`
          absolute md:relative w-full md:w-72 lg:w-80 bg-white border-r border-gray-200 flex flex-col z-20 transition-transform duration-300 ease-in-out shadow-lg md:shadow-none h-full
          ${isMobileFilterOpen ? 'translate-y-0' : '-translate-y-full md:translate-y-0'}
        `} data-id="element-1685">
          <div className="p-4 md:p-5 flex-1 overflow-y-auto" data-id="element-1686">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-1687">
              <FilterIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1688" />
              {t('applyFilter')}
            </h2>

            <div className="space-y-4" data-id="element-1689">
              <div data-id="element-1690">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1691">
                  {t('selectDistrict')}
                </label>
                <select value={selectedDistrict} onChange={e => {
                setSelectedDistrict(e.target.value);
              }} className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-[#006A4E] focus:border-[#006A4E]" data-id="element-1692">
                  <option value="" data-id="element-1693">-- {t('selectDistrict')} --</option>
                  {filterData.districts.map(d => <option key={d.name} value={d.name} data-id="element-1694">
                      {language === 'bn' ? d.nameBn : d.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-1695">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1696">
                  {t('selectUpazila')}
                </label>
                <select value={selectedUpazila} onChange={e => {
                setSelectedUpazila(e.target.value);
              }} disabled={!selectedDistrict} className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-[#006A4E] focus:border-[#006A4E] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-1697">
                  <option value="" data-id="element-1698">-- {t('selectUpazila')} --</option>
                  {availableUpazilas.map(u => <option key={u} value={u} data-id="element-1699">
                      {language === 'bn' ? filterData.upazilas[u as keyof typeof filterData.upazilas].nameBn : u}
                    </option>)}
                </select>
              </div>

              <div data-id="element-1700">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1701">
                  {t('selectMouza')}
                </label>
                <select value={selectedMouza} onChange={e => setSelectedMouza(e.target.value)} disabled={!selectedUpazila} className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-[#006A4E] focus:border-[#006A4E] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-1702">
                  <option value="" data-id="element-1703">-- {t('selectMouza')} --</option>
                  {availableMouzas.map(m => <option key={m} value={m} data-id="element-1704">
                      {language === 'bn' ? mouzaData.find(md => md.name === m)?.nameBn : m}
                    </option>)}
                </select>
              </div>

              <div className="pt-2 border-t border-gray-100" data-id="element-1705">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1706">
                  {t('searchByKhatian')}
                </label>
                <input type="text" value={searchKhatian} onChange={e => setSearchKhatian(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-[#006A4E] focus:border-[#006A4E]" placeholder="e.g. 1023" data-id="element-1707" />
              </div>

              <div data-id="element-1708">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1709">
                  {t('searchByDag')}
                </label>
                <input type="text" value={searchDag} onChange={e => setSearchDag(e.target.value)} className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-[#006A4E] focus:border-[#006A4E]" placeholder="e.g. 456" data-id="element-1710" />
              </div>
            </div>

            <div className="mt-6 flex space-x-3" data-id="element-1711">
              <button onClick={handleResetFilter} className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1712">
                {t('resetFilter')}
              </button>
              <button onClick={handleApplyFilter} className="flex-1 py-2 px-4 bg-[#006A4E] rounded-md text-sm font-medium text-white hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-1713">
                {t('applyFilter')}
              </button>
            </div>

            {/* Legend */}
            <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-1714">
              <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-1715">
                {t('legend')}
              </h3>
              <div className="space-y-2 text-sm" data-id="element-1716">
                <div className="flex items-center" data-id="element-1717">
                  <div className="w-4 h-4 rounded-sm mr-2 border border-white shadow-sm" style={{
                  backgroundColor: '#4CAF50',
                  opacity: 0.7
                }} data-id="element-1718"></div>
                  <span className="text-gray-600" data-id="element-1719">{t('agricultural')}</span>
                </div>
                <div className="flex items-center" data-id="element-1720">
                  <div className="w-4 h-4 rounded-sm mr-2 border border-white shadow-sm" style={{
                  backgroundColor: '#2196F3',
                  opacity: 0.7
                }} data-id="element-1721"></div>
                  <span className="text-gray-600" data-id="element-1722">{t('residential')}</span>
                </div>
                <div className="flex items-center" data-id="element-1723">
                  <div className="w-4 h-4 rounded-sm mr-2 border border-white shadow-sm" style={{
                  backgroundColor: '#FF9800',
                  opacity: 0.7
                }} data-id="element-1724"></div>
                  <span className="text-gray-600" data-id="element-1725">{t('commercial')}</span>
                </div>
                <div className="flex items-center mt-3" data-id="element-1726">
                  <div className="w-4 h-0 border-t-2 border-dashed border-[#006A4E] mr-2" data-id="element-1727"></div>
                  <span className="text-gray-600" data-id="element-1728">Mouza Boundary</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Map Area */}
        <div className="flex-1 relative bg-[#E8EFE0] overflow-auto" onClick={() => setActivePin(null)} data-id="element-1729">
          {activeMouza ? <div className="relative min-h-full min-w-[800px]" data-id="element-1730">
              {/* Mouza Name Banner */}
              <div className="sticky top-0 left-0 right-0 z-10 bg-white/90 backdrop-blur-sm border-b border-gray-200 px-4 py-2 flex items-center justify-between" data-id="element-1731">
                <div className="flex items-center space-x-2" data-id="element-1732">
                  <MapIcon className="w-4 h-4 text-[#006A4E]" data-id="element-1733" />
                  <span className="font-bold text-[#006A4E]" data-id="element-1734">
                    {language === 'bn' ? activeMouza.nameBn : activeMouza.name}{' '}
                    {t('mouza')}
                  </span>
                  <span className="text-xs text-gray-500" data-id="element-1735">
                    —{' '}
                    {language === 'bn' ? activeMouza.districtBn : activeMouza.district}
                    ,{' '}
                    {language === 'bn' ? activeMouza.upazilaBn : activeMouza.upazila}
                  </span>
                </div>
                <span className="text-xs text-gray-400" data-id="element-1736">
                  {language === 'bn' ? 'প্লটে ক্লিক করুন বিস্তারিত দেখতে' : 'Click a pin for details'}
                </span>
              </div>

              {/* Actual Mouza Map Image */}
              <div className="relative w-full h-auto min-h-[600px]" data-id="element-1737">
                <img src="https://cdn.magicpatterns.com/uploads/9K6dKez36KyrJFQhveVRS6/image.png" alt="Mouza Map" className="w-full h-full object-cover opacity-90" data-id="element-1738" />

                {/* Clickable Pin Markers */}
                {samplePlots.filter(p => activeMouza.plots.includes(p.id)).map(plot => {
              const isActive = activePin === plot.id;
              const isHovered = hoveredPlot === plot.id;
              // Position overrides matching the prompt
              const positions: Record<string, {
                top: string;
                left: string;
              }> = {
                'PLOT-001': {
                  top: '30%',
                  left: '18%'
                },
                'PLOT-002': {
                  top: '22%',
                  left: '42%'
                },
                'PLOT-003': {
                  top: '35%',
                  left: '30%'
                },
                'PLOT-004': {
                  top: '48%',
                  left: '65%'
                },
                'PLOT-005': {
                  top: '25%',
                  left: '58%'
                },
                'PLOT-006': {
                  top: '20%',
                  left: '15%'
                },
                'PLOT-007': {
                  top: '15%',
                  left: '40%'
                },
                'PLOT-008': {
                  top: '25%',
                  left: '70%'
                },
                'PLOT-009': {
                  top: '45%',
                  left: '50%'
                },
                'PLOT-010': {
                  top: '55%',
                  left: '20%'
                },
                'PLOT-011': {
                  top: '40%',
                  left: '80%'
                },
                'PLOT-012': {
                  top: '65%',
                  left: '35%'
                },
                'PLOT-013': {
                  top: '60%',
                  left: '65%'
                },
                'PLOT-014': {
                  top: '42%',
                  left: '12%'
                },
                'PLOT-015': {
                  top: '55%',
                  left: '85%'
                }
              };
              const pos = positions[plot.id] || {
                top: plot.mapCoords.y,
                left: plot.mapCoords.x
              };
              return <div key={plot.id} className="absolute transform -translate-x-1/2 -translate-y-full cursor-pointer group z-10 transition-transform hover:scale-110" style={{
                top: pos.top,
                left: pos.left
              }} onClick={e => handlePinClick(e, plot.id)} onMouseEnter={() => setHoveredPlot(plot.id)} onMouseLeave={() => setHoveredPlot(null)} data-id="element-1739">
                        <MapPinIcon className={`w-10 h-10 ${getPinColor(plot.type)} drop-shadow-lg transition-colors`} fill="white" data-id="element-1740" />
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-sm" data-id="element-1741"></div>

                        {/* Hover tooltip (only show if not active) */}
                        {isHovered && !isActive && <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white px-3 py-1.5 rounded shadow-lg text-xs font-medium z-30 whitespace-nowrap pointer-events-none" data-id="element-1742">
                            {language === 'bn' ? plot.nameBn : plot.name}
                            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-1743"></div>
                          </div>}
                      </div>;
            })}

                {/* Mini Card Popup */}
                {activePin && <div className="absolute z-20 transform -translate-x-1/2 -translate-y-full w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-visible animate-in fade-in zoom-in-95 duration-200" style={{
              left: (() => {
                const plot = samplePlots.find(p => p.id === activePin);
                const positions: Record<string, {
                  top: string;
                  left: string;
                }> = {
                  'PLOT-001': {
                    top: '30%',
                    left: '18%'
                  },
                  'PLOT-002': {
                    top: '22%',
                    left: '42%'
                  },
                  'PLOT-003': {
                    top: '35%',
                    left: '30%'
                  },
                  'PLOT-004': {
                    top: '48%',
                    left: '65%'
                  },
                  'PLOT-005': {
                    top: '25%',
                    left: '58%'
                  },
                  'PLOT-006': {
                    top: '20%',
                    left: '15%'
                  },
                  'PLOT-007': {
                    top: '15%',
                    left: '40%'
                  },
                  'PLOT-008': {
                    top: '25%',
                    left: '70%'
                  },
                  'PLOT-009': {
                    top: '45%',
                    left: '50%'
                  },
                  'PLOT-010': {
                    top: '55%',
                    left: '20%'
                  },
                  'PLOT-011': {
                    top: '40%',
                    left: '80%'
                  },
                  'PLOT-012': {
                    top: '65%',
                    left: '35%'
                  },
                  'PLOT-013': {
                    top: '60%',
                    left: '65%'
                  },
                  'PLOT-014': {
                    top: '42%',
                    left: '12%'
                  },
                  'PLOT-015': {
                    top: '55%',
                    left: '85%'
                  }
                };
                return positions[activePin]?.left || plot?.mapCoords.x;
              })(),
              top: `calc(${(() => {
                const plot = samplePlots.find(p => p.id === activePin);
                const positions: Record<string, {
                  top: string;
                  left: string;
                }> = {
                  'PLOT-001': {
                    top: '30%',
                    left: '18%'
                  },
                  'PLOT-002': {
                    top: '22%',
                    left: '42%'
                  },
                  'PLOT-003': {
                    top: '35%',
                    left: '30%'
                  },
                  'PLOT-004': {
                    top: '48%',
                    left: '65%'
                  },
                  'PLOT-005': {
                    top: '25%',
                    left: '58%'
                  },
                  'PLOT-006': {
                    top: '20%',
                    left: '15%'
                  },
                  'PLOT-007': {
                    top: '15%',
                    left: '40%'
                  },
                  'PLOT-008': {
                    top: '25%',
                    left: '70%'
                  },
                  'PLOT-009': {
                    top: '45%',
                    left: '50%'
                  },
                  'PLOT-010': {
                    top: '55%',
                    left: '20%'
                  },
                  'PLOT-011': {
                    top: '40%',
                    left: '80%'
                  },
                  'PLOT-012': {
                    top: '65%',
                    left: '35%'
                  },
                  'PLOT-013': {
                    top: '60%',
                    left: '65%'
                  },
                  'PLOT-014': {
                    top: '42%',
                    left: '12%'
                  },
                  'PLOT-015': {
                    top: '55%',
                    left: '85%'
                  }
                };
                return positions[activePin]?.top || plot?.mapCoords.y;
              })()} - 48px)` // Offset above the pin
            }} onClick={e => e.stopPropagation()} data-id="element-1744">
                    {/* CSS Triangle pointing down */}
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-b border-r border-gray-200 rotate-45" data-id="element-1745"></div>

                    <div className="bg-[#006A4E] h-1.5 w-full rounded-t-xl" data-id="element-1746"></div>
                    <div className="p-4 relative" data-id="element-1747">
                      <button onClick={e => {
                  e.stopPropagation();
                  setActivePin(null);
                }} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 bg-gray-50 rounded-full p-1 transition-colors" data-id="element-1748">
                        <XIcon className="w-4 h-4" data-id="element-1749" />
                      </button>

                      {(() => {
                  const plot = samplePlots.find(p => p.id === activePin)!;
                  return <div data-id="element-1750">
                            <div className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider mb-2" style={{
                      backgroundColor: getPinColor(plot.type).replace('text-[', '').replace(']', '') + '20',
                      color: getPinColor(plot.type).replace('text-[', '').replace(']', '')
                    }} data-id="element-1751">
                              {t(plot.type as any)}
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 pr-6 leading-tight mb-1" data-id="element-1752">
                              {language === 'bn' ? plot.nameBn : plot.name}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4 flex items-center" data-id="element-1753">
                              <MapPinIcon className="w-3 h-3 mr-1" data-id="element-1754" />
                              {language === 'bn' ? plot.districtBn : plot.district}
                            </p>

                            <div className="mb-4 space-y-2" data-id="element-1755">
                              <div className="flex justify-between items-center bg-green-50 px-2.5 py-1.5 rounded border border-green-100" data-id="element-1756">
                                <span className="text-xs text-gray-600" data-id="element-1757">
                                  {t('cloNo') || 'CLO No.'}
                                </span>
                                <span className="text-xs font-bold text-green-700" data-id="element-1758">
                                  {plot.cloId || 'Not Generated'}
                                </span>
                              </div>
                              <div className="flex justify-between items-center px-2.5 py-1" data-id="element-1759">
                                <span className="text-xs text-gray-500" data-id="element-1760">
                                  {t('ownerName') || 'Owner Name'}
                                </span>
                                <span className="text-xs font-semibold text-gray-900 truncate max-w-[120px] text-right" data-id="element-1761">
                                  {language === 'bn' ? (plot as any).ownerNameBn || plot.nameBn : (plot as any).ownerName || plot.name}
                                </span>
                              </div>
                              <div className="flex justify-between items-center px-2.5 py-1 bg-gray-50 rounded" data-id="element-1762">
                                <span className="text-xs text-gray-500" data-id="element-1763">
                                  {t('surveyName') || 'Survey Name'}
                                </span>
                                <span className="text-xs font-semibold text-gray-900" data-id="element-1764">
                                  {language === 'bn' ? (plot as any).surveyNameBn || 'বিআরএস' : (plot as any).surveyName || 'BRS'}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm mb-5" data-id="element-1765">
                              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100" data-id="element-1766">
                                <span className="text-gray-500 text-xs block mb-0.5" data-id="element-1767">
                                  {t('khatian')}
                                </span>
                                <span className="font-bold text-gray-900" data-id="element-1768">
                                  {plot.khatian}
                                </span>
                              </div>
                              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100" data-id="element-1769">
                                <span className="text-gray-500 text-xs block mb-0.5" data-id="element-1770">
                                  {t('dag')}
                                </span>
                                <span className="font-bold text-gray-900" data-id="element-1771">
                                  {plot.dag}
                                </span>
                              </div>
                            </div>

                            <button onClick={() => handleViewDetails(plot.id)} className="w-full py-2.5 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors text-sm flex items-center justify-center shadow-sm" data-id="element-1772">
                              {t('viewDetails')}
                              <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-1773" />
                            </button>
                          </div>;
                })()}
                    </div>
                  </div>}
              </div>
            </div> : <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-id="element-1774">
              {/* Default state: show the mouza map image faded as preview */}
              <div className="absolute inset-0 opacity-15" data-id="element-1775">
                <img src="https://cdn.magicpatterns.com/uploads/9K6dKez36KyrJFQhveVRS6/image.png" alt="Mouza Map Preview" className="w-full h-full object-cover" data-id="element-1776" />
              </div>
              <div className="relative z-10" data-id="element-1777">
                <div className="w-24 h-24 bg-white/80 rounded-full flex items-center justify-center mb-4 shadow-sm border border-green-200" data-id="element-1778">
                  <MapIcon className="w-10 h-10 text-[#006A4E] opacity-50" data-id="element-1779" />
                </div>
                <h2 className="text-2xl font-bold text-[#004D3A] mb-2" data-id="element-1780">
                  {t('selectMouzaPrompt')}
                </h2>
                <p className="text-green-800 max-w-md" data-id="element-1781">
                  {language === 'bn' ? 'বাম পাশের ফিল্টার প্যানেল থেকে জেলা, উপজেলা এবং মৌজা নির্বাচন করুন।' : 'Use the filter panel on the left to select a District, Upazila, and Mouza to view the interactive cadastral map.'}
                </p>
              </div>
            </div>}

          {/* Map Controls */}
          <div className="absolute bottom-6 right-6 flex flex-col space-y-2 z-10" data-id="element-1782">
            <button className="w-10 h-10 bg-white rounded-md shadow-md flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-[#006A4E] font-bold text-xl" data-id="element-1783">
              +
            </button>
            <button className="w-10 h-10 bg-white rounded-md shadow-md flex items-center justify-center text-gray-700 hover:bg-gray-50 hover:text-[#006A4E] font-bold text-xl" data-id="element-1784">
              -
            </button>
          </div>
        </div>
      </div>
    </div>;
}